import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

/**
 * authStore.ts
 * Holds the authenticated user's identity and roles.
 * Populated by useCurrentUser hook after MSAL login completes.
 */

export interface AuthState {
  email: string
  alias: string
  displayName: string
  roles: string[]
  tenant: string
  /** All tenant values from user.Tenant (comma-separated in API, e.g. "DUWL,DUIN") */
  availableTenants: string[]
  isAuthenticated: boolean
}

interface AuthActions {
  setUser: (user: Omit<AuthState, 'isAuthenticated'>) => void
  /** Update only the active tenant (used by Header tenant dropdown) */
  setTenant: (tenant: string) => void
  clearUser: () => void
}

const initialState: AuthState = {
  email: '',
  alias: '',
  displayName: '',
  roles: [],
  tenant: '',
  availableTenants: [],
  isAuthenticated: false,
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set) => ({
      ...initialState,
      setUser: (user) => set({ ...user, isAuthenticated: true }),
      setTenant: (tenant) => set((s) => ({ ...s, tenant })),
      clearUser: () => set(initialState),
    }),
    {
      name: 'du-auth',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
)
